import type { Account, Tank } from "./api";
import { isPage, type NavNode, type NavPage, pathOf, walk } from "./nav";

export const PER_SECTION = 8;
export const RECENT_MAX = 5;

export interface JumpItem {
  key: string;
  label: string;
  detail?: string;
  path: string;
}

export interface JumpSection {
  id: "pinned" | "places" | "recent" | "accounts" | "tanks";
  title: string;
  items: JumpItem[];
  /** The count of matches after the cut at PER_SECTION. */
  more?: number;
}

export interface Visit {
  path: string;
}

const compact = (text: string) => text.toLowerCase().replace(/[\s-]+/g, "");

/** 0 is the best match: the exact number, then a prefix, then a word start, then any part. */
export function rank(query: string, texts: string[], number?: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  if (number !== undefined && compact(number) === compact(q)) return 0;
  const lower = texts.map((t) => t.toLowerCase());
  if (number !== undefined && compact(number).startsWith(compact(q))) return 1;
  if (lower.some((t) => t.startsWith(q))) return 1;
  if (lower.some((t) => t.split(/[\s\-/·,.]+/).some((word) => word.startsWith(q)))) return 2;
  if (number !== undefined && compact(number).includes(compact(q))) return 3;
  if (lower.some((t) => t.includes(q))) return 3;
  return null;
}

function best<T>(rows: T[], score: (row: T) => number | null, label: (row: T) => string): { rows: T[]; more: number } {
  const found = rows
    .map((row) => ({ row, at: score(row) }))
    .filter((match): match is { row: T; at: number } => match.at !== null)
    .sort((a, b) => a.at - b.at || label(a.row).localeCompare(label(b.row)));
  return { rows: found.slice(0, PER_SECTION).map((match) => match.row), more: Math.max(0, found.length - PER_SECTION) };
}

const KEYWORD_ONLY = 4;

/** A match on the page or group label comes before a match on a keyword only. */
function placeRank(query: string, { above, page }: Place): number | null {
  if (page.formerly?.includes(query.trim().toLowerCase())) return -1;
  const named = rank(query, [page.label, ...above.map((group) => group.label)]);
  if (named !== null) return named;
  const keyword = rank(query, [...above.flatMap((group) => group.keywords ?? []), ...(page.keywords ?? [])]);
  return keyword === null ? null : keyword + KEYWORD_ONLY;
}

function section(id: JumpSection["id"], title: string, found: { rows: JumpItem[]; more: number }): JumpSection {
  return found.more ? { id, title, items: found.rows, more: found.more } : { id, title, items: found.rows };
}

interface Place {
  above: NavNode[];
  page: NavPage;
  item: JumpItem;
}

/** Each place of the tree; the detail is its groups, for example "Manage". */
function places(tree: readonly NavNode[], base: string): Place[] {
  return walk(tree).flatMap(({ node, above }) => {
    if (!isPage(node)) return [];
    const detail = above.map((group) => group.label).join(" / ");
    return [{ above, page: node, item: { key: `place:${node.id}`, label: node.label, detail, path: `${base}${pathOf(node)}` } }];
  });
}

export const placeItems = (tree: readonly NavNode[], base: string) => places(tree, base).map((place) => place.item);

const accountItem = (base: string, a: Account): JumpItem => ({
  key: `account:${a.id}`,
  label: `${a.number} · ${a.name}`,
  detail: a.address || undefined,
  path: `${base}/accounts/${encodeURIComponent(a.id)}`,
});

const tankItem = (base: string, t: Tank): JumpItem => ({
  key: `tank:${t.id}`,
  label: t.label,
  detail: "Container",
  path: `${base}/tanks/${encodeURIComponent(t.id)}`,
});

export function search(
  query: string,
  menu: readonly NavNode[],
  base: string,
  accounts: Account[],
  tanks: Tank[],
): JumpSection[] {
  const placeMatches = best(places(menu, base), (place) => placeRank(query, place), ({ page }) => page.label);
  const accountMatches = best(accounts, (a) => rank(query, [a.name, a.address ?? ""], a.number), (a) => a.number);
  const tankMatches = best(tanks, (t) => rank(query, [t.label, ...t.devices]), (t) => t.label);
  const sections: JumpSection[] = [
    section("places", "Places", { rows: placeMatches.rows.map((place) => place.item), more: placeMatches.more }),
    section("accounts", "Accounts", { rows: accountMatches.rows.map((a) => accountItem(base, a)), more: accountMatches.more }),
    section("tanks", "Containers", { rows: tankMatches.rows.map((t) => tankItem(base, t)), more: tankMatches.more }),
  ];
  return sections.filter((section) => section.items.length > 0);
}

/** Recent visits as menu items. A visit whose target is gone or hidden is left out. */
export function recentItems(
  visits: Visit[],
  menu: readonly NavNode[],
  base: string,
  accounts: Account[] | undefined,
  tanks: Tank[] | undefined,
): JumpItem[] {
  const known = new Map<string, JumpItem>();
  for (const item of placeItems(menu, base)) known.set(item.path, item);
  for (const a of accounts ?? []) known.set(accountItem(base, a).path, accountItem(base, a));
  for (const t of tanks ?? []) known.set(tankItem(base, t).path, tankItem(base, t));
  const items = visits.map((visit) => known.get(visit.path)).filter((item): item is JumpItem => item !== undefined);
  return items.map((item) => ({ ...item, key: `recent:${item.key}` }));
}

/** Put the newest visit first, with no copy of the same path, and keep only the last few. */
export function remember(visits: Visit[], path: string): Visit[] {
  return [{ path }, ...visits.filter((visit) => visit.path !== path)].slice(0, RECENT_MAX);
}

export function readVisits(raw: string | null): Visit[] {
  try {
    const found: unknown = JSON.parse(raw ?? "[]");
    if (!Array.isArray(found)) return [];
    return found
      .filter((v): v is Visit => typeof v === "object" && v !== null && typeof (v as Visit).path === "string")
      .slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}
